/**
 * HTML snippets for mid-post CTA cards in blog content.
 * Paste these into blogPosts.ts content strings. Use 1-2 per post after key sections.
 *
 * Usage: Insert the snippet into the content string where you want the card to appear.
 * Example: content: `<p>...</p>${blogCardSnippets.tryNow}<p>...</p>`
 */

export const blogCardSnippets = {
  /** Hear your business answer the phone in 60 seconds. Best for: how-to, comparisons */
  tryNow: `
      <div class="not-prose my-8 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm">
        <div class="flex items-start gap-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-600">
            <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-lg font-bold text-gray-900 mt-0 mb-1">Hear your business answer the phone in 60 seconds.</p>
            <p class="mt-1 mb-1 text-gray-700">Enter your website URL. We'll build an AI that knows your services, hours, and policies. Then <em>call it</em>. No signup required.</p>
            <a href="/try" class="inline-flex items-center gap-2 font-semibold text-blue-700 hover:text-blue-900">
              Try it now no signup required
            </a>
          </div>
        </div>
      </div>
    `.trim(),

  /** Cost of missed calls. Pass industry for personalized headline, e.g. getMissedCallCost("dental") */
  getMissedCallCost: (industry?: string) =>
    `
      <div class="not-prose my-8 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm">
        <div class="flex items-start gap-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-amber-600">
            <span class="text-xl font-bold text-white">$</span>
          </div>
          <div class="min-w-0 flex-1">
            <h4 class="text-lg font-bold text-gray-900 mt-0">What's the real cost of every call you missed${industry ? ` in ${industry}` : ''}?</h4>
            <p class="mt-1 mb-1 text-gray-700">The average service call is worth $150-$2,000. Miss 5 a week? That's $40K+ a year walking to your competitor.</p>
            <a href="/never-miss-a-call" class="mt-4 inline-flex items-center gap-2 font-semibold text-amber-800 hover:text-amber-900">
              See what you're losing-and how to stop it
            </a>
          </div>
        </div>
      </div>
    `.trim(),

  /** Is your business ready? 5 signs. Best for: consideration-phase, general how-to */
  readyForAI: `
      <div class="not-prose my-8 rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-6 shadow-sm">
        <div class="flex items-start gap-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-green-600">
            <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h4 class="text-lg font-bold text-gray-900">Is your business ready for an AI receptionist?</h4>
            <p class="mt-2 text-gray-700">5 signs you're leaving money on the table-and 2 when you might want to wait. Take 2 minutes to find out.</p>
            <a href="/blog/5-signs-ready-for-ai-receptionist" class="mt-4 inline-flex items-center gap-2 font-semibold text-green-800 hover:text-green-900">
              Take the 2-minute check
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>
      </div>
    `.trim(),
};
